import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './NotificationBell.css';

export default function NotificationBell() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const dropdownRef = useRef(null);
    const dropdownPortalRef = useRef(null);
    const buttonRef = useRef(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

    const fetchNotifications = async () => {
        try {
            const res = await fetch('/api/notifications/logs?unread=false');
            const data = await res.json();
            if (data.status === 'success') {
                setNotifications(data.logs);
                const unread = data.logs.filter(n => !n.is_read).length;
                setUnreadCount(unread);
            }
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        }
    };

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 60000);
        return () => clearInterval(interval);
    }, []);

    const updateDropdownPos = useCallback(() => {
        const btn = buttonRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        const top = Math.min(rect.bottom + 8, window.innerHeight - 16);
        const right = Math.max(8, Math.round(window.innerWidth - rect.right));
        setDropdownPos({ top, right });
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            const target = event.target;
            const inAnchor = dropdownRef.current && dropdownRef.current.contains(target);
            const inPortal = dropdownPortalRef.current && dropdownPortalRef.current.contains(target);
            if (!inAnchor && !inPortal) {
                setIsOpen(false);
                setSelectedNotification(null);
            }
        }

        function handleKeydown(event) {
            if (event.key === 'Escape') {
                if (selectedNotification) {
                    setSelectedNotification(null);
                } else {
                    setIsOpen(false);
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeydown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [selectedNotification]);

    // Mark single notification as read
    const markAsRead = async (notification) => {
        if (notification.is_read) return;

        try {
            await fetch('/api/notifications/mark-read', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: [notification.id] })
            });
            // Optimistic update
            setNotifications(prev => prev.map(n =>
                n.id === notification.id ? { ...n, is_read: 1 } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Mark read failed:', err);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await fetch('/api/notifications/mark-read', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: 'all' })
            });
            setNotifications(notifications.map(n => ({ ...n, is_read: 1 })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Mark all read failed:', err);
        }
    };

    // Delete all notifications
    const handleDeleteAll = async () => {
        if (!window.confirm('Tüm bildirimler silinecek. Emin misiniz?')) return;

        try {
            const res = await fetch('/api/notifications/delete', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: 'all' })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setNotifications([]);
                setUnreadCount(0);
            }
        } catch (err) {
            console.error('Delete all failed:', err);
        }
    };

    // Handle notification click - mark as read and show popup
    const handleNotificationClick = (notification) => {
        markAsRead(notification);
        setSelectedNotification(notification);
    };

    // Navigate to orders page
    const handleGoToOrders = () => {
        setIsOpen(false);
        setSelectedNotification(null);
        navigate('/orders');
    };

    // Navigate to specific order (if order number available)
    const handleViewOrder = (orderNumber) => {
        setIsOpen(false);
        setSelectedNotification(null);
        navigate(`/orders?search=${orderNumber}`);
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            fetchNotifications();
            updateDropdownPos();
            setSelectedNotification(null);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        updateDropdownPos();
        window.addEventListener('resize', updateDropdownPos);
        window.addEventListener('scroll', updateDropdownPos, true);
        return () => {
            window.removeEventListener('resize', updateDropdownPos);
            window.removeEventListener('scroll', updateDropdownPos, true);
        };
    }, [isOpen, updateDropdownPos]);

    const typeLabels = useMemo(() => ({
        new_order: 'Yeni Sipariş',
        low_stock: 'Stok Uyarısı',
        order_shipped: 'Sipariş Kargoya Verildi',
        order_delivered: 'Sipariş Teslim Edildi',
        payment_received: 'Ödeme Alındı',
        question_received: 'Yeni Soru'
    }), []);

    const typeIcons = useMemo(() => ({
        new_order: 'fas fa-shopping-cart',
        low_stock: 'fas fa-exclamation-triangle',
        order_shipped: 'fas fa-truck',
        order_delivered: 'fas fa-check-circle',
        payment_received: 'fas fa-money-bill-wave',
        question_received: 'fas fa-question-circle'
    }), []);

    const parseNotification = useCallback((n) => {
        const title = typeLabels[n.type] || String(n.type || '').toUpperCase();
        const icon = typeIcons[n.type] || 'fas fa-bell';
        const storeName = n.recipient || '';
        const when = n.created_at ? new Date(n.created_at).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' }) : '';
        const timeAgo = n.created_at ? getTimeAgo(new Date(n.created_at)) : '';

        const raw = (n.message || '').replace(/\bNaN\b/g, '0').replace(/\bundefined\b/g, '').replace(/\s{2,}/g, ' ').trim();

        let orderNo = '';
        let store = storeName;
        let customer = '';
        let amount = '';

        if (n.type === 'new_order') {
            const parts = raw.split(' - ').map((p) => p.trim()).filter(Boolean);

            if (parts.length >= 1) {
                orderNo = parts[0].replace(/^Yeni Sipariş:\s*/i, '').trim();
            }
            if (parts.length >= 2) {
                store = parts[1] || store;
            }
            if (parts.length >= 3) {
                customer = parts[2] || '';
            }
            if (parts.length >= 4) {
                amount = parts[3] || '';
            }
        }

        return {
            title,
            icon,
            orderNo,
            store,
            customer,
            amount,
            when,
            timeAgo,
            raw
        };
    }, [typeLabels, typeIcons]);

    // Time ago helper
    const getTimeAgo = (date) => {
        const now = new Date();
        const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diff < 60) return 'Az önce';
        if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
        if (diff < 604800) return `${Math.floor(diff / 86400)} gün önce`;
        return date.toLocaleDateString('tr-TR');
    };

    // Render notification detail popup
    const renderDetailPopup = () => {
        if (!selectedNotification) return null;

        const parsed = parseNotification(selectedNotification);

        return (
            <div className="notification-detail-overlay" onClick={() => setSelectedNotification(null)}>
                <div className="notification-detail-popup" onClick={(e) => e.stopPropagation()}>
                    <div className="notification-detail-header">
                        <div className="notification-detail-icon">
                            <i className={parsed.icon}></i>
                        </div>
                        <div className="notification-detail-title-wrapper">
                            <h3>{parsed.title}</h3>
                            <span className="notification-detail-time">{parsed.when}</span>
                        </div>
                        <button className="notification-detail-close" onClick={() => setSelectedNotification(null)}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    <div className="notification-detail-body">
                        {selectedNotification.type === 'new_order' ? (
                            <div className="notification-detail-info">
                                <div className="notification-detail-row">
                                    <span className="notification-detail-label">🏪 Mağaza</span>
                                    <span className="notification-detail-value">{parsed.store || '-'}</span>
                                </div>
                                <div className="notification-detail-row">
                                    <span className="notification-detail-label">📦 Sipariş No</span>
                                    <span className="notification-detail-value notification-detail-value--code">{parsed.orderNo || '-'}</span>
                                </div>
                                <div className="notification-detail-row">
                                    <span className="notification-detail-label">👤 Müşteri</span>
                                    <span className="notification-detail-value">{parsed.customer || 'Belirtilmemiş'}</span>
                                </div>
                                <div className="notification-detail-row">
                                    <span className="notification-detail-label">💰 Tutar</span>
                                    <span className="notification-detail-value notification-detail-value--amount">{parsed.amount || '-'}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="notification-detail-message">
                                {parsed.raw}
                            </div>
                        )}
                    </div>

                    <div className="notification-detail-actions">
                        {selectedNotification.type === 'new_order' && parsed.orderNo && (
                            <button
                                className="notification-action-btn notification-action-btn--primary"
                                onClick={() => handleViewOrder(parsed.orderNo)}
                            >
                                <i className="fas fa-eye"></i>
                                Siparişi Görüntüle
                            </button>
                        )}
                        <button
                            className="notification-action-btn notification-action-btn--secondary"
                            onClick={handleGoToOrders}
                        >
                            <i className="fas fa-list"></i>
                            Siparişlere Git
                        </button>
                        <button
                            className="notification-action-btn notification-action-btn--ghost"
                            onClick={() => setSelectedNotification(null)}
                        >
                            Kapat
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="notification-bell-container" ref={dropdownRef}>
            <button ref={buttonRef} className="notification-bell-btn" onClick={toggleDropdown} title="Bildirimler">
                <i className="fas fa-bell"></i>
                {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
            </button>

            {isOpen && createPortal(
                <div
                    className="notification-dropdown notification-dropdown--portal"
                    ref={dropdownPortalRef}
                    style={{ top: `${dropdownPos.top}px`, right: `${dropdownPos.right}px` }}
                    role="dialog"
                    aria-label="Bildirimler"
                >
                    <div className="notification-header">
                        <h3>
                            <i className="fas fa-bell"></i>
                            Bildirimler
                            {unreadCount > 0 && <span className="notification-header-count">{unreadCount}</span>}
                        </h3>
                        <div className="notification-header-actions">
                            {unreadCount > 0 && (
                                <button className="mark-read-btn" onClick={handleMarkAllRead} title="Tümünü Okundu İşaretle">
                                    <i className="fas fa-check-double"></i>
                                    Okundu
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button className="delete-all-btn" onClick={handleDeleteAll} title="Tüm Bildirimleri Sil">
                                    <i className="fas fa-trash-alt"></i>
                                    Sil
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="notification-list">
                        {notifications.length > 0 ? (
                            notifications.map((notification) => {
                                const parsed = parseNotification(notification);
                                return (
                                    <div
                                        key={notification.id}
                                        className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="notification-item-icon">
                                            <i className={parsed.icon}></i>
                                        </div>
                                        <div className="notification-item-content">
                                            <div className="notification-item-header">
                                                <span className="notification-item-title">{parsed.title}</span>
                                                {parsed.store && (
                                                    <span className="notification-chip notification-chip--store">{parsed.store}</span>
                                                )}
                                            </div>

                                            {notification.type === 'new_order' ? (
                                                <div className="notification-item-details">
                                                    {parsed.orderNo && <span className="notification-order-no">#{parsed.orderNo}</span>}
                                                    {parsed.customer && parsed.customer !== 'Müşteri' && (
                                                        <span className="notification-customer">{parsed.customer}</span>
                                                    )}
                                                    {parsed.amount && <span className="notification-amount">{parsed.amount}</span>}
                                                </div>
                                            ) : (
                                                <div className="notification-item-message">
                                                    {parsed.raw.substring(0, 60)}{parsed.raw.length > 60 ? '...' : ''}
                                                </div>
                                            )}

                                            <div className="notification-item-meta">
                                                <span className="notification-time">{parsed.timeAgo}</span>
                                                {!notification.is_read && <span className="notification-new-badge">Yeni</span>}
                                            </div>
                                        </div>
                                        <div className="notification-item-arrow">
                                            <i className="fas fa-chevron-right"></i>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="notification-empty">
                                <i className="fas fa-inbox"></i>
                                <p>Bildirim bulunmuyor</p>
                            </div>
                        )}
                    </div>

                    <div className="notification-footer">
                        <button className="notification-footer-btn" onClick={handleGoToOrders}>
                            <i className="fas fa-external-link-alt"></i>
                            Tüm Siparişleri Görüntüle
                        </button>
                    </div>

                    {/* Detail popup inside portal */}
                    {renderDetailPopup()}
                </div>,
                document.body
            )}
        </div>
    );
}
